import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, Typography, Space, Tag, Input, Result, Button, Skeleton, Empty } from 'antd';
import { SearchOutlined, CalendarOutlined, PlusOutlined, PlayCircleOutlined, ArrowRightOutlined } from '@ant-design/icons';
import PageHeader from '../Layout/PageHeader';
import { tns } from '../../i18n/common';

const { Title, Text, Paragraph } = Typography;

const ReleasesList = () => {
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [size] = useState(10);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');
    const [user, setUser] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [locale, setLocale] = useState(localStorage.getItem('locale') || 'en');
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
    const sentinelRef = useRef(null);

    const isAdmin = () => {
        const rr = (user?.azure_roles || []).map(r => String(r).toLowerCase());
        const orr = (user?.azure_organization_roles || []).map(r => String(r).toLowerCase());
        return rr.includes('admin') || orr.includes('admin');
    };

    const fetchData = async (p = 1, append = false) => {
        setLoading(true);
        try {
            const [u, res] = await Promise.all([
                axios.post('/user').catch(() => ({ data: null })),
                axios.get(`/v1/releases`, {
                    params: { page: p, size, locale, q: query }
                })
            ]);
            setUser(u?.data || null);
            const data = res.data?.data || [];
            const pg = res.data?.pagination || { total: 0, page: p, size };
            setTotal(pg.total || 0);
            setPage(pg.page || p);
            if (append) {
                setItems(prev => [...prev, ...data]);
            } else {
                setItems(data);
            }
            const accumulated = (append ? items.length + data.length : data.length);
            setHasMore(accumulated < (pg.total || 0));
        } catch (e) {
            console.error('Failed to load releases', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(1, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, locale]);

    // Listen for locale changes
    useEffect(() => {
        const handleLocaleChange = () => {
            const newLocale = localStorage.getItem('locale') || 'en';
            setLocale(newLocale);
        };
        window.addEventListener('locale-changed', handleLocaleChange);
        return () => window.removeEventListener('locale-changed', handleLocaleChange);
    }, []);

    // Track viewport width for responsive banner behavior
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Infinite scroll sentinel
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const observer = new IntersectionObserver((entries) => {
            const first = entries[0];
            if (first.isIntersecting && hasMore && !loading) {
                fetchData(page + 1, true);
            }
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, [sentinelRef, hasMore, loading, page]);

    const onSearch = () => fetchData(1, false);

    return (
                <div style={{
            backgroundColor: '#fafafa',
            minHeight: '100vh'
        }}>
            <PageHeader
                title={tns('releases', 'title')}
                subtitle={tns('releases', 'subtitle')}
                actions={isAdmin() && (
                    <Link to="/admin/releases/new">
                        <Button type="primary" icon={<PlusOutlined />} size="large">
                            {tns('releases', 'newRelease')}
                        </Button>
                    </Link>
                )}
            >
                <Input.Search
                    placeholder={tns('releases', 'searchPlaceholder')}
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    onSearch={onSearch}
                    allowClear
                    size="large"
                    prefix={<SearchOutlined />}
                    style={{ maxWidth: 400 }}
                />
            </PageHeader>

            {/* Content Section */}
            <div style={{ padding: '24px 24px' }}>
                <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                {(!loading && items.length === 0) ? (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '400px'
                        }}>
                            <Empty
                                description={
                                    <div style={{ textAlign: 'center' }}>
                                        <Title level={4} style={{ color: '#8c8c8c' }}>{tns('releases', 'noReleasesYet')}</Title>
                                        <Text type="secondary">
                                            {tns('releases', 'noReleasesDescription')}
                                        </Text>
                                    </div>
                                }
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            >
                                {isAdmin() && (
                                    <Link to="/admin/releases/new">
                                        <Button type="primary" icon={<PlusOutlined />}>
                                            {tns('releases', 'createFirstRelease')}
                                        </Button>
                                    </Link>
                                )}
                            </Empty>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                            {/* Video Tutorial Banner */}
                            <Card
                                style={{
                                    background: '#E6F4FF',
                                    border: '1px solid #91CAFF',
                                    borderRadius: 8,
                                    boxShadow: 'none'
                                }}
                                styles={{ body: { padding: 0 } }}
                            >
                                <div style={{
                                    padding: !isMobile ? '20px 24px' : '16px 20px'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: !isMobile ? 'row' : 'column',
                                        alignItems: !isMobile ? 'center' : 'stretch',
                                        gap: !isMobile ? 20 : 12,
                                        textAlign: !isMobile ? 'left' : 'center'
                                    }}>
                                        {/* Content */}
                                        <div style={{ flex: 1 }}>
                                                                                        <Title level={4} style={{
                                                color: '#1f2937',
                                                margin: 0,
                                                marginBottom: 4,
                                                fontWeight: 600,
                                                fontSize: 18
                                            }}>
                                                {tns('releases', 'videoBannerTitle', locale)}
                                            </Title>
                                            <Text style={{
                                                color: '#6b7280',
                                                fontSize: 14,
                                                lineHeight: 1.4
                                            }}>
                                                {tns('releases', 'videoBannerDescription', locale)}
                                            </Text>
                                        </div>

                                        {/* CTA Button */}
                                        <Link to="/videos" style={{ display: isMobile ? 'block' : undefined, width: isMobile ? '100%' : 'auto' }}>
                                            <Button
                                                type="primary"
                                                icon={<ArrowRightOutlined />}
                                                iconPosition="end"
                                                style={{
                                                    background: '#000000',
                                                    border: '1px solid #000000',
                                                    color: 'white',
                                                    fontWeight: 500,
                                                    height: 36,
                                                    paddingLeft: 16,
                                                    paddingRight: 16,
                                                    borderRadius: 6,
                                                    fontSize: 14,
                                                    width: isMobile ? '100%' : undefined,
                                                    marginTop: isMobile ? 8 : 0
                                                }}
                                                block={isMobile}
                                                onMouseEnter={(e) => {
                                                    e.target.style.background = '#333333';
                                                    e.target.style.borderColor = '#333333';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.background = '#000000';
                                                    e.target.style.borderColor = '#000000';
                                                }}
                                            >
                                                {tns('releases', 'videoBannerButton', locale)}
                                            </Button>
                                        </Link>
                                    </div>
                                </div>
                            </Card>

                            {items.map((item) => (
                                                                <div key={item.id} style={{
                                    display: 'grid',
                                    gridTemplateColumns: '140px 1fr',
                                    gap: 24
                                }}>
                                    {/* Version Card */}
                                    <div>
                                                                                <Card style={{
                                            height: '100%',
                                            textAlign: 'left',
                                            background: 'transparent',
                                            border: 'none',
                                            boxShadow: 'none'
                                        }} styles={{ body: { padding: 0 } }}>
                                            <div style={{
                                                fontWeight: 700,
                                                fontSize: 20,
                                                marginBottom: 8,
                                                color: '#1a1a1a'
                                            }}>
                                                v{item.version || '—'}
                                            </div>
                                            {item.published_at && (
                                                <div style={{
                                                    fontSize: 11,
                                                    color: '#888',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 4
                                                }}>
                                                    <CalendarOutlined />
                                                    {(() => {
                                                        const d = new Date(item.published_at);
                                                        const day = String(d.getDate()).padStart(2, '0');
                                                        const month = String(d.getMonth() + 1).padStart(2, '0');
                                                        const year = d.getFullYear();
                                                        const hours = String(d.getHours()).padStart(2, '0');
                                                        const minutes = String(d.getMinutes()).padStart(2, '0');
                                                        return `${day}.${month}.${year} ${hours}:${minutes}`;
                                                    })()}
                                                </div>
                                            )}
                                        </Card>
                                    </div>

                                    {/* Content Card */}
                                    <div>
                                    <Link to={`/releases/${item.slug}`} style={{ textDecoration: 'none' }}>
                                            <Card
                                                hoverable
                                                style={{
                                                    height: '100%',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                                    border: '1px solid #f0f0f0',
                                                    transition: 'all 0.3s ease'
                                                }}
                                                styles={{ body: { padding: 20 } }}
                                            >
                                                <div style={{ display: 'flex', gap: 16 }}>
                                                    {/* Hero Image */}
                                                    <div style={{
                                                        width: 120,
                                                        height: 80,
                                                        borderRadius: 8,
                                                        overflow: 'hidden',
                                                        flexShrink: 0,
                                                        background: '#f5f5f5'
                                                    }}>
                                                        <img
                                                            src={item.hero_image_url || '/images/placeholder/release-placeholder.jpg'}
                                                            alt={item.title || ''}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover'
                                                            }}
                                                        />
                                                    </div>

                                                    {/* Text Content */}
                                                    <div style={{ flex: 1 }}>
                                                        <Title level={4} style={{
                                                            marginTop: 0,
                                                            marginBottom: 8,
                                                            fontSize: 18,
                                                            fontWeight: 600,
                                                            color: '#1a1a1a',
                                                            lineHeight: 1.3
                                                        }}>
                                                            {item.title || item.slug}
                                                        </Title>

                                                        {item.summary && (
                                                            <div>
                                                                <style>{`
                                                                    .release-card-html ul { list-style: disc; margin: 0 0 6px 16px; padding-left: 16px; }
                                                                    .release-card-html ol { list-style: decimal; margin: 0 0 6px 16px; padding-left: 16px; }
                                                                    .release-card-html li { margin-bottom: 4px; }
                                                                    .release-card-html p { margin: 0 0 8px; }
                                                                `}</style>
                                                                <div
                                                                    className="release-card-html"
                                                                    style={{
                                                                        color: '#666',
                                                                        marginBottom: 12,
                                                                        fontSize: 14,
                                                                        lineHeight: 1.5
                                                                    }}
                                                                    dangerouslySetInnerHTML={{ __html: item.summary }}
                                                                />
                                                            </div>
                                                        )}

                                                                                                                <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 8,
                                                            marginTop: 'auto'
                                                        }}>
                                                            {item.published_at && (
                                                                <Text type="secondary" style={{ fontSize: 11 }}>
                                                                    {new Date(item.published_at).toLocaleDateString()}
                                                                </Text>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                        </Card>
                                    </Link>
                                    </div>
                                </div>
                            ))}

                            {/* Loading Skeleton */}
                            {(loading && items.length > 0) && (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '140px 1fr',
                                    gap: 24
                                }}>
                                    <Skeleton.Input active style={{ width: '100%', height: 100 }} />
                                    <Skeleton active paragraph={{ rows: 3 }} />
                                </div>
                            )}

                            {/* Infinite Scroll Sentinel */}
                            <div ref={sentinelRef} style={{ height: 20 }} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReleasesList;
